import type { FileNode } from "@openanalyst-ai/sdk/v2"

type WatcherEvent = {
  type: string
  properties: unknown
}

type WatcherOps = {
  normalize: (input: string) => string
  hasFile: (path: string) => boolean
  isOpen?: (path: string) => boolean
  loadFile: (path: string) => void
  node: (path: string) => FileNode | undefined
  isDirLoaded: (path: string) => boolean
  refreshDir: (path: string) => void
  /** Invalidate a cached-but-not-currently-loaded dir so the next open re-fetches. */
  markStale: (path: string) => void
}

export function invalidateFromWatcher(event: WatcherEvent, ops: WatcherOps) {
  if (event.type !== "file.watcher.updated") return
  const props =
    typeof event.properties === "object" && event.properties ? (event.properties as Record<string, unknown>) : undefined
  const rawPath = typeof props?.file === "string" ? props.file : undefined
  const kind = typeof props?.event === "string" ? props.event : undefined
  if (!rawPath) return
  if (!kind) return

  const path = ops.normalize(rawPath)
  if (!path) return
  if (path.startsWith(".git/")) return

  if (ops.hasFile(path) || ops.isOpen?.(path)) {
    ops.loadFile(path)
  }

  if (kind === "change") {
    const dir = (() => {
      if (path === "") return ""
      const node = ops.node(path)
      if (node?.type !== "directory") return
      return path
    })()
    if (dir === undefined) return
    // Loaded dir → refresh live; otherwise invalidate its cache so the next
    // open re-fetches instead of silently dropping the change.
    if (ops.isDirLoaded(dir)) ops.refreshDir(dir)
    else ops.markStale(dir)
    return
  }
  if (kind !== "add" && kind !== "unlink") return

  const parent = path.split("/").slice(0, -1).join("/")
  // Loaded parent → refresh live; otherwise mark it stale so a re-open re-fetches.
  // Dirs the user has never opened have no cache entry, so markStale is a no-op for
  // them — they already re-fetch fresh on first open, and we never refetch eagerly.
  if (ops.isDirLoaded(parent)) ops.refreshDir(parent)
  else ops.markStale(parent)
}
